import { format, parseISO } from "date-fns"
import PropTypes from "prop-types"
import React, { useCallback, useMemo, useState } from "react"
import { ExportSalesAction } from "src/actions/pointOfSale"
import { useMarina } from "src/contexts/MarinaContext"

import BoatPreviewLink from "src/components/BoatPreviewLink"
import Chips from "src/components/Chips"
import DataTableV2 from "src/components/DataTableV2"
import Form from "src/components/Form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"

import {
  exportSaleCheckouts,
  getSaleCheckoutsDatatable,
  getSaleCheckoutsSelectableIds,
  printSaleCheckouts,
} from "src/api/PointOfSale/SaleCheckouts"

import useDatatable from "src/hooks/use_datatable"
import useDebounce from "src/hooks/use_debounce"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import StatusBadge from "./SaleHistory/StatusBadge"

const GUEST_DISPLAY_NAME = "Guest"

const DEFAULT_FILTERS = {
  search: "",
  layout: [],
  soldBy: [],
  startDate: "",
  endDate: "",
}

const SalesContainer = ({ layouts, marinaUsers }) => {
  const { marinaSlug, presentmentCurrency } = useMarina()
  const showToast = useToast()
  const tracker = useTracker()

  const table = useDatatable({
    queryKey: ["saleCheckoutsDatatable", marinaSlug],
    fetchFn: (params) => getSaleCheckoutsDatatable(marinaSlug, params),
    defaultFilters: DEFAULT_FILTERS,
    fetchAllIds: (params) =>
      getSaleCheckoutsSelectableIds(marinaSlug, params).then((r) => r.ids),
    infinite: true,
    idKey: "id",
    syncFiltersToUrl: true,
  })

  // Search input — local value for immediate feedback, debounced for API
  const [searchInputValue, setSearchInputValue] = useState(
    table.filters.search || ""
  )
  const [debouncedSetSearch] = useDebounce((value) => {
    table.setFilter("search", value)
  })
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value
      setSearchInputValue(value)
      debouncedSetSearch(value.trim())
    },
    [debouncedSetSearch]
  )

  // Date filter handlers
  const handleStartDateChange = useCallback(
    (date) => {
      table.setFilter("startDate", date ? format(date, "yyyy-MM-dd") : "")
    },
    [table]
  )

  const handleEndDateChange = useCallback(
    (date) => {
      table.setFilter("endDate", date ? format(date, "yyyy-MM-dd") : "")
    },
    [table]
  )

  const parseFilterDate = (dateStr) => {
    if (!dateStr) return null
    try {
      return parseISO(dateStr)
    } catch {
      return null
    }
  }

  // Infinite scroll handler
  const handleScroll = (e) => {
    if (!table.hasNextPage || table.isFetchingNextPage) return
    const { scrollHeight, scrollTop, clientHeight } = e.target
    if (scrollHeight - scrollTop - 200 <= clientHeight) {
      table.fetchNextPage()
    }
  }

  // Bulk export handler
  const handleBulkExport = async (selectedIds) => {
    if (!selectedIds?.length) return

    try {
      await exportSaleCheckouts(marinaSlug, { ids: selectedIds })
      table.setSelection([])
      showToast("Export is being generated and will be emailed shortly.", {
        type: "success",
      })
    } catch {
      showToast("Unable to start export. Please try again.", {
        type: "error",
      })
    }
  }

  const actions = [ExportSalesAction({ onExport: handleBulkExport })]

  // Layout options from server-rendered props
  const layoutOptions = useMemo(
    () => layouts.map((l) => ({ value: String(l.id), label: l.name })),
    [layouts]
  )

  // Marina user options from server-rendered props
  const marinaUserOptions = useMemo(
    () =>
      marinaUsers.map((u) => ({
        value: String(u.id),
        label: u.name || u.email,
      })),
    [marinaUsers]
  )

  const columns = [
    {
      key: "saleEncodedId",
      header: "ID",
      width: "120px",
      sortable: true,
      sortKey: "encodedId",
      render: (row) => `#${row.saleEncodedId}`,
      cardRole: "title",
    },
    {
      key: "createdAt",
      header: "Date",
      width: "130px",
      sortable: true,
      render: (row) => {
        const date = parseISO(row.createdAt)
        return (
          <div>
            <div>{format(date, "MM/dd/yyyy")}</div>
            <div className="text-xs text-gray-600">{format(date, "pp")}</div>
          </div>
        )
      },
    },
    {
      key: "customer",
      header: "Customer",
      sortable: true,
      render: (row) => (
        <div className="truncate" title={row.saleCustomerName}>
          {row.saleCustomerName || GUEST_DISPLAY_NAME}
        </div>
      ),
    },
    {
      key: "boatName",
      header: "Boat",
      width: "160px",
      sortable: true,
      sortKey: "boatName",
      render: (row) => {
        if (!row.contactBoat) {
          return <span className="text-gray-400">&mdash;</span>
        }
        return <BoatPreviewLink boat={row.contactBoat} />
      },
    },
    {
      key: "soldBy",
      header: "Sold By",
      sortable: true,
      render: (row) => {
        return <div className="truncate">{row.soldBy}</div>
      },
    },
    {
      key: "layoutName",
      header: "Layout",
      width: "130px",
      sortable: true,
      sortKey: "layout",
      render: (row) => {
        if (!row.layout) return null
        return (
          <span className={row.layout.isDeleted ? "text-gray-600" : ""}>
            {row.layout.name}
            {row.layout.isDeleted ? " (deleted)" : ""}
          </span>
        )
      },
    },
    {
      key: "total",
      header: "Total",
      width: "100px",
      render: (row) =>
        formatCurrencyFromMinorUnits({
          amount: row.total,
          currency: presentmentCurrency,
        }),
    },
    {
      key: "saleStatus",
      header: "Status",
      render: (row) => <StatusBadge status={row.saleStatus} />,
    },
  ]

  const rowConfig = {
    variant: () => "default",
    getViewUrl: (row) =>
      `/manage/${marinaSlug}/point_of_sale/sales/${row.saleEncodedId}?checkout=${row.id}`,
    onViewClick: () => {
      tracker.trackEvent("sale_history:sale_detail_viewed")
    },
  }

  // Filter chips
  const renderChips = () => {
    const chips = []

    if (table.filters.search) {
      chips.push(
        <Chips.Chip
          key="search"
          text={`Search: ${table.filters.search}`}
          onClick={() => {
            table.clearFilter("search")
            setSearchInputValue("")
          }}
        />
      )
    }

    table.filters.layout.forEach((layoutId) => {
      const layout = layouts.find((l) => String(l.id) === String(layoutId))
      if (layout) {
        chips.push(
          <Chips.Chip
            key={`layout-${layoutId}`}
            text={`Layout: ${layout.name}`}
            onClick={() =>
              table.setFilter(
                "layout",
                table.filters.layout.filter((id) => id !== layoutId)
              )
            }
          />
        )
      }
    })

    table.filters.soldBy.forEach((userId) => {
      const user = marinaUsers.find((u) => String(u.id) === String(userId))
      if (user) {
        chips.push(
          <Chips.Chip
            key={`sold-by-${userId}`}
            text={`Sold By: ${user.name || user.email}`}
            onClick={() =>
              table.setFilter(
                "soldBy",
                table.filters.soldBy.filter((id) => id !== userId)
              )
            }
          />
        )
      }
    })

    if (table.filters.startDate || table.filters.endDate) {
      const dateText = [table.filters.startDate, table.filters.endDate]
        .filter(Boolean)
        .join(" – ")
      chips.push(
        <Chips.Chip
          key="dateRange"
          text={`Date: ${dateText}`}
          onClick={() => {
            table.setFilters({ startDate: "", endDate: "" })
          }}
        />
      )
    }

    if (chips.length === 0) return null
    return (
      <div className="mb-4 pl-1">
        <Chips>{chips}</Chips>
      </div>
    )
  }

  return (
    <div className="w-full p-4 pb-8 pl-8">
      <div className="mb-4 flex w-full flex-wrap items-end gap-2">
        <div className="w-80">
          <Form.IconTextField
            id="sale-history-search"
            icon={<i className="fa-kit fa-search-fa" />}
            name="search"
            value={searchInputValue}
            onChange={handleSearchChange}
            placeholder="Search by Customer, Boat Name, or Sale ID"
          />
        </div>
        <Select
          multiple
          value={table.filters.soldBy}
          onValueChange={(values) => table.setFilter("soldBy", values)}
        >
          <SelectTrigger variant="button">Sold By</SelectTrigger>
          <SelectContent>
            {marinaUserOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          multiple
          value={table.filters.layout}
          onValueChange={(values) => table.setFilter("layout", values)}
        >
          <SelectTrigger variant="button">Layout</SelectTrigger>
          <SelectContent>
            {layoutOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-end gap-2">
          <div>
            <Form.Label htmlFor="start-date-filter">Start Date</Form.Label>
            <Form.DatePicker
              id="start-date-filter"
              onChange={handleStartDateChange}
              value={parseFilterDate(table.filters.startDate)}
              dateFormat="MM/dd/yyyy"
              maxDate={parseFilterDate(table.filters.endDate)}
            />
          </div>
          <div>
            <Form.Label htmlFor="end-date-filter">End Date</Form.Label>
            <Form.DatePicker
              id="end-date-filter"
              onChange={handleEndDateChange}
              value={parseFilterDate(table.filters.endDate)}
              dateFormat="MM/dd/yyyy"
              minDate={parseFilterDate(table.filters.startDate)}
            />
          </div>
        </div>
      </div>
      {renderChips()}
      <DataTableV2
        name="saleHistory"
        columns={columns}
        data={table.records}
        actions={actions}
        rowConfig={rowConfig}
        rowCount={8}
        maxHeight="calc(100vh - 250px)"
        onScroll={handleScroll}
        onPrint={(ids) => printSaleCheckouts(marinaSlug, { ids })}
        {...table.tableProps}
      />
    </div>
  )
}

SalesContainer.propTypes = {
  layouts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  marinaUsers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      email: PropTypes.string,
    })
  ),
}

export default SalesContainer
